import { BehaviorSubject } from "rxjs";
import { SCXML, SingleOrArray, Event } from "xstate";
import { useEffect } from "react";
import { ScheduleUrlData, ScheduleQueryParamPairs, ScheduleQueryParam, getScheduleIdFromUrlPathname } from "./schedule-url-helpers";
import { ScheduleMachineEvent } from "./schedule.machine.types";
import { ScheduleSearchParams } from "./schedule-search-params";
import { FilterType } from "./filter-rules";

type ScheduleMachineSend = (event: SCXML.Event<ScheduleMachineEvent> | SingleOrArray<Event<ScheduleMachineEvent>>) => void;

const isScheduleQueryParam = (key: string): key is ScheduleQueryParam => (Object.values(ScheduleQueryParam) as string[]).includes(key);

export const useScheduleUrlSubscription = (send: ScheduleMachineSend, locationData$: BehaviorSubject<ScheduleUrlData>) => {
    useEffect(() => {
        const sub = scheduleUrlSub(send, locationData$);
        return () => {
            sub.unsubscribe();
        };
    }, []);
};

/** Subscribes to url location changes and sends appropriate events to the machine in which it is invoked */
export const scheduleUrlSub = (send: ScheduleMachineSend, locationData$: BehaviorSubject<ScheduleUrlData>) => {
    /** The current query string */
    let currentQueryString = locationData$.getValue().query.toString();

    /** The current pathname */
    let currentPathname = locationData$.getValue().pathname;

    const subscription = locationData$.subscribe((newLocationData) => {
        const eventsToSend: Parameters<ScheduleMachineSend>[0] = [];
        /*
            PATHNAME UPDATES
        */

        if (currentPathname !== newLocationData.pathname) {
            const currentScheduleIdFromUrl = getScheduleIdFromUrlPathname(currentPathname);
            const newScheduleId = getScheduleIdFromUrlPathname(newLocationData.pathname);
            if (currentScheduleIdFromUrl !== newScheduleId) {
                // If the schedule id has changed, tell the machine to update the selected root schedule!
                eventsToSend.push({ type: "FROM_URL_SELECT_ROOT_SCHEDULE_VERSION", payload: newScheduleId });
            }
            currentPathname = newLocationData.pathname;
        }

        /*
            QUERY PARAM UPDATES
        */

        /** Params that have been updated in the query string or added to the query string */
        const updatedParams: ScheduleQueryParamPairs[] = [];
        /** Params that have been removed from the query string */
        const removedParams: ScheduleQueryParam[] = [];
        /** The current query */
        const currentSearchParam = new ScheduleSearchParams(currentQueryString);
        /** The new query */
        const newSearchParam = newLocationData.query;

        currentSearchParam.forEach((value, key) => {
            if (!isScheduleQueryParam(key)) {
                return;
            }
            const newValue = newSearchParam.get(key);
            if (value === newValue) {
                // If the values are equal, we don't care
                return;
            }
            if (newValue == null) {
                // If no new value is found, we must remove it
                removedParams.push(key);
            }
        });

        newSearchParam.forEach((value, key) => {
            if (!isScheduleQueryParam(key)) {
                return;
            }
            const oldValue = currentSearchParam.get(key);
            if (value === oldValue) {
                // If the values are equal, we don't care
                return;
            }
            // If it reaches here, it must mean the param has been added or updated
            updatedParams.push([key, value] as ScheduleQueryParamPairs);
        });

        // Handle the removed params
        removedParams.forEach((key) => {
            switch (key) {
                case ScheduleQueryParam.ViewBy: {
                    eventsToSend.push({ type: "UPDATE_VIEW_BY", payload: { filterType: FilterType.DayVenue } });
                    break;
                }
                case ScheduleQueryParam.Table: {
                    eventsToSend.push({ type: "FROM_URL_UPDATE_IS_TABLE_VIEW", payload: false });
                    break;
                }
                default: {
                    break;
                }
            }
        });
        // Hanlde the updated/added params
        updatedParams.forEach(([key, value]) => {
            switch (key) {
                case ScheduleQueryParam.ViewBy: {
                    eventsToSend.push({ type: "FROM_URL_UPDATE_VIEW_BY", payload: { filterType: value } });
                    break;
                }
                case ScheduleQueryParam.Table: {
                    eventsToSend.push({ type: "FROM_URL_UPDATE_IS_TABLE_VIEW", payload: value === "true" });
                    break;
                }
                case ScheduleQueryParam.CalendarLevel: {
                    eventsToSend.push({ type: "FROM_URL_UPDATE_CALENDAR_LEVEL", payload: Number(value) });
                    break;
                }
                case ScheduleQueryParam.VenueLocation: {
                    eventsToSend.push({ type: "FROM_URL_UPDATE_SELECTED_VENUE_LOCATION", payload: value });
                    break;
                }
                case ScheduleQueryParam.DataField: {
                    eventsToSend.push({ type: "FROM_URL_UPDATE_SELECTED_DATA_FIELD_LIST_ITEMS", payload: value });
                    break;
                }
                case ScheduleQueryParam.Day: {
                    eventsToSend.push({ type: "FROM_URL_UPDATE_DAY", payload: Number(value) });
                    break;
                }
                default: {
                    break;
                }
            }
        });
        if (eventsToSend.length > 0) {
            send(eventsToSend);
        }
        currentQueryString = newLocationData.query.toString();
    });

    return subscription;
};
